import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const readMoreClassName =
  "group mt-[18px] inline-flex h-[30px] w-fit min-w-[113px] items-center justify-center gap-[8px] rounded-full border border-[#bc8639] bg-transparent px-3 font-['Poppins',sans-serif] text-[10px] font-medium leading-[15.82px] tracking-[0] text-[#bc8639] no-underline transition-all duration-300 ease-out hover:-translate-y-[1px] hover:bg-[#bc8639] hover:text-white hover:shadow-[0_8px_18px_rgba(188,134,57,0.22)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#bc8639] active:translate-y-0";

const readMoreIconClassName = 'transition-transform duration-300 ease-out group-hover:translate-x-[2px]';

const ELLIPSIS = '...';

const formatPressTitleMobile = (title) => {
  const text = String(title || '').trim();
  if (!text || /[\u0600-\u06FF]/.test(text)) return text;
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 10 && text.length <= 88) return text;

  let used = 0;
  let chars = 0;
  while (used < words.length && chars + words[used].length + (used ? 1 : 0) <= 72) {
    chars += words[used].length + (used ? 1 : 0);
    used += 1;
  }
  if (used < 6) used = Math.min(6, words.length);

  const thirdLineWords = words.slice(used, used + 2);
  const visible = words.slice(0, used).concat(thirdLineWords);
  if (visible.length >= words.length) return text;
  return `${visible.join(' ')}${ELLIPSIS}`;
};

const NewsArticleCard = ({
  publisherLogoSrc,
  publisherLogoAlt = '',
  publisherLogoText = '',
  imageSrc,
  imageAlt = '',
  title,
  summary,
  readMoreHref,
  onReadMore,
  onPlayClick,
  watchNowIconSrc,
  readMoreLabel = 'Read more',
  readMoreAriaLabel = 'Read more',
  readMoreNewTab = false,
  hidePublisherLogo = false,
  className = '',
  titleClassName = '',
  summaryClassName = '',
  contentDir = 'auto',
  imageFit = 'cover',
  imageObjectPosition = 'center',
  layout = 'default',
  isVideoPlaying = false,
  youtubeId = '',
}) => {
  const isPressStyleLayout = layout === 'pressDesktop' || layout === 'videoDesktop';

  const readMoreContent = (
    <>
      {readMoreLabel}
      {isPressStyleLayout ? (
        <span aria-hidden="true"> →</span>
      ) : (
        <ChevronRight size={14} strokeWidth={1.5} aria-hidden className={readMoreIconClassName} />
      )}
    </>
  );

  const pressDesktopReadMoreClassName =
    'press-release-desktop-card__read-more group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#bc8639]';

  let readMoreControl;
  const activeReadMoreClassName = isPressStyleLayout ? pressDesktopReadMoreClassName : readMoreClassName;
  if (readMoreHref) {
    const isExternalHref = /^https?:\/\//i.test(readMoreHref);
    readMoreControl = isExternalHref ? (
      <a
        href={readMoreHref}
        className={activeReadMoreClassName}
        aria-label={readMoreAriaLabel}
        {...(readMoreNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {readMoreContent}
      </a>
    ) : (
      <Link to={readMoreHref} className={activeReadMoreClassName} aria-label={readMoreAriaLabel}>
        {readMoreContent}
      </Link>
    );
  } else {
    readMoreControl = (
      <button
        type="button"
        className={activeReadMoreClassName}
        aria-label={readMoreAriaLabel}
        onClick={onReadMore}
      >
        {readMoreContent}
      </button>
    );
  }

  if (isPressStyleLayout) {
    const showLogo =
      layout === 'pressDesktop' &&
      !hidePublisherLogo &&
      (publisherLogoSrc || publisherLogoText);
    const useYoutubeWatchCta = layout === 'videoDesktop' && watchNowIconSrc;

    const youtubeWatchControl = useYoutubeWatchCta ? (
      readMoreHref ? (
        <a
          href={readMoreHref}
          className="mc-events-video-card__watch"
          aria-label={readMoreAriaLabel}
          {...(readMoreNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          <img src={watchNowIconSrc} alt="" className="mc-events-video-card__youtube-icon h-[10px] w-[13px] object-contain lg:h-[14px] lg:w-[14px]" width={14} height={14} />
          Watch Now
        </a>
      ) : (
        <button
          type="button"
          className="mc-events-video-card__watch"
          aria-label={readMoreAriaLabel}
          onClick={onReadMore}
        >
          <img src={watchNowIconSrc} alt="" className="mc-events-video-card__youtube-icon h-[10px] w-[13px] object-contain lg:h-[14px] lg:w-[14px]" width={14} height={14} />
          Watch Now
        </button>
      )
    ) : null;

    const mobilePressTitle =
      layout === 'pressDesktop' ? formatPressTitleMobile(title) : title;

    return (
      <article
        className={`press-release-desktop-card ${imageFit === 'contain' ? 'press-release-desktop-card--media-contain' : ''
          } ${className}`.trim()}
      >
        <div className="press-release-desktop-card__contain">
          <div className="press-release-desktop-card__media">
            <img
              src={imageSrc}
              alt={imageAlt}
              className={imageFit === 'contain' ? 'object-contain' : 'object-cover'}
              style={{ objectPosition: imageObjectPosition }}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                const el = event.currentTarget;
                if (el.src.includes('/maxresdefault.jpg')) {
                  el.src = el.src.replace('/maxresdefault.jpg', '/hq720.jpg');
                } else if (el.src.includes('/hq720.jpg')) {
                  el.src = el.src.replace('/hq720.jpg', '/mqdefault.jpg');
                }
              }}
            />
            {layout === 'videoDesktop' && isVideoPlaying && youtubeId ? (
              <iframe
                className="mc-video-card__inline-player"
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
                title={imageAlt || title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : null}
            {layout === 'videoDesktop' && onPlayClick && !isVideoPlaying ? (
              <button
                type="button"
                className="mc-video-card__play"
                aria-label={readMoreAriaLabel}
                onClick={onPlayClick}
              >
                <svg className="mc-video-card__play-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M7 4.78659C7 3.73814 8.15848 3.10651 9.04351 3.66699L18.8217 9.8804C19.6582 10.4121 19.6582 11.5879 18.8217 12.1196L9.04351 18.333C8.15848 18.8935 7 18.2619 7 17.2134V4.78659Z" fill="white" />
                </svg>
              </button>
            ) : null}
          </div>

          <div className="press-release-desktop-card__body">
            {showLogo ? (
              <div className="press-release-desktop-card__logo-wrap">
                {publisherLogoText ? (
                  <p className="press-release-desktop-card__logo-text">{publisherLogoText}</p>
                ) : (
                  <img
                    src={publisherLogoSrc}
                    alt={publisherLogoAlt}
                    className="press-release-desktop-card__logo"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
            ) : null}

            <h2
              dir={contentDir}
              className={`press-release-desktop-card__title ${layout === 'pressDesktop' ? '' : 'line-clamp-3'
                } ${layout === 'videoDesktop' ? 'mt-0' : ''} ${titleClassName}`.trim()}
            >
              {layout === 'pressDesktop' ? (
                <>
                  <span className="lg:hidden">{mobilePressTitle}</span>
                  <span className="hidden lg:block press-release-desktop-card__title-desktop">
                    {title}
                  </span>
                </>
              ) : (
                title
              )}
            </h2>

            <p
              dir={contentDir}
              className={`press-release-desktop-card__summary ${layout === 'videoDesktop'
                ? 'line-clamp-2'
                : layout === 'pressDesktop'
                  ? 'line-clamp-3 lg:line-clamp-2'
                  : 'line-clamp-4'
                } ${summaryClassName}`.trim()}
            >
              {summary}
            </p>

            <div
              className={`press-release-desktop-card__read-more-wrap${useYoutubeWatchCta
                ? ' press-release-desktop-card__read-more-wrap--youtube'
                : ''
                }`}
            >
              {useYoutubeWatchCta ? youtubeWatchControl : readMoreControl}
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className={`flex h-full flex-col rounded-[16px] bg-white p-[22px] ${className}`.trim()}>
      {!hidePublisherLogo && (publisherLogoSrc || publisherLogoText) ? (
        <div className="flex h-[52px] shrink-0 items-center justify-center">
          {publisherLogoText ? (
            <p className="m-0 text-center font-['Lato',sans-serif] text-[14px] font-bold leading-[18px] text-[#0a0a0a]">
              {publisherLogoText}
            </p>
          ) : (
            <img
              src={publisherLogoSrc}
              alt={publisherLogoAlt}
              className="max-h-full max-w-[190px] object-contain"
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
      ) : null}

      <div
        className={`${hidePublisherLogo || (!publisherLogoSrc && !publisherLogoText) ? 'mt-0' : 'my-6'} aspect-video w-full shrink-0 overflow-hidden rounded-[4px] bg-white`}
      >
        <img
          src={imageSrc}
          alt={imageAlt}
          className={`block h-full w-full object-center ${imageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
          style={{ objectPosition: imageObjectPosition }}
          loading="lazy"
          decoding="async"
        />
      </div>

      <h2
        dir={contentDir}
        className={`mt-[18px] line-clamp-3 min-h-[5.9rem] font-['Poppins'] text-[20px] font-light leading-[29.53px] tracking-[0] text-black ${titleClassName}`.trim()}
      >
        {title}
      </h2>

      <p
        dir={contentDir}
        className={`mt-[8px] line-clamp-3 min-h-[4.05rem] font-['Poppins'] text-[14px] font-light leading-[1.45] tracking-[0] text-[#0A0A0AC2] ${summaryClassName}`.trim()}
      >
        {summary}
      </p>

      <div className="mt-auto">{readMoreControl}</div>
    </article>
  );
};

export default NewsArticleCard;
